import React, { useRef, useState, useCallback, useEffect } from 'react';
import { TextInput, required, AutocompleteInput } from 'react-admin';
import { Grid } from '@material-ui/core/';
import { useFormState, useForm, Field } from 'react-final-form';
import toArray from 'lodash/toArray';
import { withStyles } from '@material-ui/core/styles';
import RichTextInput from '../../core/components/inputs/RichTextInput';
import CheckBoxField from '../../core/components/CheckBoxField';
import {
  mailTemplateTypes,
  passthroughTemplateType,
  bodyVariables,
  subjectVariables,
  mupVariables,
  passthroughSubjectVariables,
  passthroughBodyVariables
} from './MailTemplateLookups';
import { fieldLengthValidator } from '../../core/utils/index';
import FieldTitleWithListPopover from '../../core/components/inputs/FieldTitleWithListPopover';
import EmailTemplateApi from '../../core/apis/EmailTemplateApi';
import TenantConfigurationApi from '../../core/apis/TenantConfigurationApi';

const styles = () => ({
  subjectRoot: {
    marginBottom: '0px !important'
  },
  inputLabel: {
    width: '100%'
  },
  value: {
    marginTop: '-3px'
  }
});

const MailTemplateSummary = props => {
  const [formErrors, setFormErrors] = useState({});
  const formState = useFormState();
  const form = useForm();
  const subject = useRef(null);
  const { classes, usedForAutomatedEmail, record } = props;

  // Verify that Email Template description is unique
  const validateDescription = useCallback(
    async (value, allValues, props) => {
      if (record && value === record.description) {
        return;
      }
      if (props.active) {
        let error;

        const descExists = await EmailTemplateApi.checkDescriptionExists(value);
        if (descExists) {
          error = 'Email Template with this description already exists';
        } else {
          error = undefined;
        }
        setFormErrors({ ...formErrors, description: error });
        return error;
      }
      return formErrors.description;
    },
    [record, formErrors]
  );

  const [templateChoices, setTemplateChoices] = useState([]);

  useEffect(() => {
    const fetchTemplates = async () => {
      const passThroughEnabled =
        (await TenantConfigurationApi.get('Enable_Passthrough'))?.pageData[0]
          ?.value === '1';
      if (passThroughEnabled) {
        setTemplateChoices(mailTemplateTypes.concat(passthroughTemplateType));
      } else {
        setTemplateChoices(mailTemplateTypes);
      }
    };
    fetchTemplates();
  }, []);

  const isMupChild = sessionStorage.getItem('isMupChild') === 'true';

  const getVariablesToUse = (type, isMupChild) => {
    if (type === 'PASSTHROUGH') {
      return {
        subject: passthroughSubjectVariables,
        body: passthroughBodyVariables
      };
    }
    if (isMupChild) {
      return {
        subject: subjectVariables.concat(mupVariables),
        body: bodyVariables.concat(mupVariables)
      };
    }
    return {
      subject: subjectVariables,
      body: bodyVariables
    };
  };

  function handleSelectVariableSubject(variable) {
    let currentValue = toArray(formState.values.subject || '');
    const cursorIndex = subject.current.selectionStart || 0;
    currentValue.splice(cursorIndex, 0, variable.value);

    form.change('subject', currentValue.join(''));
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12} md={12} lg={8}>
        <TextInput
          source="description"
          label="Description"
          validate={[
            required(),
            fieldLengthValidator(150),
            validateDescription
          ]}
          options={{ autoComplete: 'off' }}
          variant="standard"
          disabled={usedForAutomatedEmail}
          helperText={false}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={4}>
        <AutocompleteInput
          source="type"
          label="Email Type"
          choices={templateChoices}
          validate={[required()]}
          variant="standard"
          disabled={usedForAutomatedEmail}
          helperText={false}
          options={{
            suggestionsContainerProps: {
              style: {
                backgroundColor: 'white',
                border: '1px solid #E0E0E0',
                boxShadow:
                  ' 0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)'
              }
            }
          }}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={12}>
        <TextInput
          key={formState.values.type}
          source="subject"
          options={{
            autoComplete: 'off',
            label: (
              <FieldTitleWithListPopover
                source="subject"
                label="Subject"
                onSelectItem={handleSelectVariableSubject}
                items={
                  getVariablesToUse(formState.values.type, isMupChild).subject
                }
                displayingVariables={!usedForAutomatedEmail}
              />
            )
          }}
          InputLabelProps={{
            classes: {
              root: classes.inputLabel
            }
          }}
          variant="standard"
          inputRef={subject}
          helperText={false}
          classes={
            {
              /**root: classes.subjectRoot*/
            }
          }
          validate={[fieldLengthValidator(300)]}
          disabled={usedForAutomatedEmail}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={12}>
        <Field
          name="useFundEmail"
          component={CheckBoxField}
          label="Use Fund Contact Email As From Email Address"
          variant="standard"
          type="checkbox"
        />
      </Grid>
      <Grid item xs={12} sm={12} md={12}>
        <RichTextInput
          key={formState.values.type}
          source="body"
          label="Body"
          classes={{ value: classes.value }}
          variables={getVariablesToUse(formState.values.type, isMupChild).body}
          disabled={usedForAutomatedEmail}
          displayingVariables={!usedForAutomatedEmail}
        />
      </Grid>
    </Grid>
  );
};

export default withStyles(styles)(MailTemplateSummary);
